import { useCallback, useEffect, useState } from 'react';
import type { MapLayerMouseEvent, ViewStateChangeEvent } from 'react-map-gl';
import { Map as MapBox, useMap, NavigationControl } from 'react-map-gl';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';

import { theme } from '../../styles/theme';
import { HeatmapLegend } from '../HeatMapLegend';
import { NccpLegend } from '../HeatMapLegend/NccpLegend';
import { StatusLegend } from '../HeatMapLegend/StatusLegend';
import { HeatMapModeToggle } from '../HeatMapModeToggle';
import { CountryCard } from '../Card/CountryCard';
import { Icon, type IconVariant } from '../Icon';
import { Typography } from '../Typography';
import { parseTooltipText } from '../../utils/parse-tooltip-text';
import type {
  HeatMapCountryMetaData,
  HeatMapRange,
  LongitudeLatitude,
  MapBoxGecodingResponse,
} from '../../map-data/map-data-types';
import type { LegendMeta } from '../../map-data/heatmap-legend-config';
import {
  heatMapLegends,
  getStatusConfigByDataValue,
} from '../../map-data/heatmap-legend-config';
import { useMapData } from '../../map-data/use-map-data';
import { useHeatMapSelectedLocations } from '../HeatMapExperience/HeatMapSelectedLocationsProvider';
import { useUpdateNavigationBar } from '../NavigationBar/NavigationBarProvider';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';

import { HeatMapSource } from './HeatMapSource';
import { HeatMapStatusFilter } from './HeatMapStatusFilter';
import { HeatMapYearTimeline } from './HeatMapYearTimeline';

const MapContainer = styled.div`
  position: relative;
`;

const mapBoxToken = process.env.REACT_APP_MAP_BOX_TOKEN;

type Props = {
  visible?: boolean;
  drawerOpen?: boolean;
};

export function capitalize(value: string): string {
  return value[0].toUpperCase() + value.slice(1).toLowerCase();
}

const mapMovementDuration = 1600; // milliseconds

export type HeatMapMeta = Record<HeatMapRange, LegendMeta>;

export function HeatMap(props: Props) {
  const [countryInfo, setCountryInfo] = useState<HeatMapCountryMetaData>();
  const [offsetCard, setOffsetCard] = useState(false);
  const { error: errorFetchingMapData, countryHeatMapMetaData } = useMapData();
  const { heatMap } = useMap();
  const { t } = useTranslation();

  const [hoveredIso2, setHoveredIso2] = useState<string | null>(null);

  const {
    filterOptions,
    filterTypeStatus,
    setRangeType,
    rangeType,
    setFilterTypeSex,
    filterTypeSex,
  } = useHeatMapFilterOptions();

  const isNccpMode = rangeType === 'nccp';
  const isStatusMode = rangeType === 'status';

  const interactiveLayerIds = isNccpMode
    ? [
        'country-boundaries-nccp-0',
        'country-boundaries-nccp-1',
        'country-boundaries-nccp-2',
        'country-boundaries-nccp-3',
        'country-boundaries-nccp-4',
      ]
    : isStatusMode
    ? [
        'country-boundaries-status-unknownStatus',
        'country-boundaries-status-implementationResearch',
        'country-boundaries-status-formalCommitment',
        'country-boundaries-status-implementationRollout',
      ]
    : [
        'country-boundaries-60',
        'country-boundaries-50',
        'country-boundaries-40',
        'country-boundaries-30',
        'country-boundaries-20',
        'country-boundaries-10',
      ];

  const heatMapMeta: HeatMapMeta = {
    incidence: {
      description: parseTooltipText(t('heatMap.lungCancerCases')),
    },
    mortality: {
      description: parseTooltipText(t('heatMap.lungCancerDeaths')),
    },
    nccp: {
      description: '',
    },
    status: {
      description: '',
    },
  };

  const {
    setShowCountryCard,
    showCountryCard,
    selectedCountryCode,
    setSelectedCountryCode,
  } = useHeatMapSelectedLocations();

  // Only compute legendConfig for incidence/mortality modes
  const legendConfig =
    rangeType === 'nccp' || rangeType === 'status'
      ? null
      : heatMapLegends[rangeType][filterTypeSex];
  const legendMeta = heatMapMeta[rangeType];

  const { updateSelectedNavigationItem } = useUpdateNavigationBar();

  // reset map is resized when it becomes visible
  useEffect(() => {
    if (props.visible) {
      heatMap?.resize();
      setOffsetCard(false);
    }
  }, [props.visible, heatMap]);

  // Resize and re-center map when drawer state changes
  useEffect(() => {
    if (props.drawerOpen !== undefined && heatMap) {
      // Delay to allow CSS transition to complete
      const timeoutId = setTimeout(() => {
        heatMap.resize();

        // Re-center on current country if selected
        if (
          selectedCountryCode &&
          countryHeatMapMetaData?.[selectedCountryCode]
        ) {
          const countryMetaData = countryHeatMapMetaData[selectedCountryCode];
          const { boundaryBox } = countryMetaData;

          if (boundaryBox) {
            const bounds: [[number, number], [number, number]] = [
              [boundaryBox[0] ?? 0, boundaryBox[1] ?? 0],
              [boundaryBox[2] ?? 0, boundaryBox[3] ?? 0],
            ];

            // Use responsive padding - max 140px but no more than 20% of viewport
            const maxPadding = Math.min(
              140,
              Math.floor(window.innerHeight * 0.2),
            );
            const padding = Math.max(20, maxPadding);

            const currentZoom = heatMap.getZoom();
            const targetCamera = heatMap.cameraForBounds(bounds, { padding });

            // Guard against NaN values
            if (!targetCamera || !Number.isFinite(targetCamera.zoom)) {
              const centerLng = (bounds[0][0] + bounds[1][0]) / 2;
              const centerLat = (bounds[0][1] + bounds[1][1]) / 2;
              heatMap.flyTo({
                center: [centerLng, centerLat],
                duration: 800,
              });
              return;
            }

            const targetZoom = targetCamera.zoom;

            if (currentZoom <= targetZoom) {
              heatMap.fitBounds(bounds, { padding, duration: 800 });
            } else {
              const centerLng = (bounds[0][0] + bounds[1][0]) / 2;
              const centerLat = (bounds[0][1] + bounds[1][1]) / 2;
              heatMap.flyTo({
                center: [centerLng, centerLat],
                duration: 800,
              });
            }
          }
        }
      }, 450); // Match CSS transition duration

      return () => clearTimeout(timeoutId);
    }
  }, [props.drawerOpen, heatMap, selectedCountryCode, countryHeatMapMetaData]);

  const moveMapToLongLat = useCallback(
    (longLat: LongitudeLatitude) => {
      const zoom = heatMap?.getZoom();
      heatMap?.flyTo({
        center: longLat,
        essential: true,
        zoom: zoom,
        duration: mapMovementDuration,
      });
    },
    [heatMap],
  );

  useEffect(() => {
    const countryMetaData = countryHeatMapMetaData?.[selectedCountryCode ?? ''];
    const { boundaryBox } = countryMetaData ?? {};

    // Set countryInfo when selectedCountryCode changes
    if (selectedCountryCode && countryMetaData) {
      setCountryInfo(countryMetaData);
    } else {
      setCountryInfo(undefined);
    }

    // if there is a selected country code and related boundary box the zoom
    // into that (country)
    if (boundaryBox) {
      const bounds: [[number, number], [number, number]] = [
        [boundaryBox[0] ?? 0, boundaryBox[1] ?? 0],
        [boundaryBox[2] ?? 0, boundaryBox[3] ?? 0],
      ];

      // Use responsive padding - max 140px but no more than 20% of viewport
      const maxPadding = Math.min(140, Math.floor(window.innerHeight * 0.2));
      const padding = Math.max(20, maxPadding); // minimum 20px padding

      const currentZoom = heatMap?.getZoom() ?? 0;
      const targetCamera = heatMap?.cameraForBounds(bounds, { padding });

      // Guard against NaN values from cameraForBounds
      if (!targetCamera || !Number.isFinite(targetCamera.zoom)) {
        // Fallback: just pan to center of bounds
        const centerLng = (bounds[0][0] + bounds[1][0]) / 2;
        const centerLat = (bounds[0][1] + bounds[1][1]) / 2;
        heatMap?.flyTo({
          center: [centerLng, centerLat],
          duration: 800,
        });
        return;
      }

      const targetZoom = targetCamera.zoom;

      // Only use fitBounds if it would zoom in; otherwise just pan to center
      if (currentZoom <= targetZoom) {
        heatMap?.fitBounds(bounds, { padding, duration: 800 });
      } else {
        // Pan to center of bounds without changing zoom
        const centerLng = (bounds[0][0] + bounds[1][0]) / 2;
        const centerLat = (bounds[0][1] + bounds[1][1]) / 2;
        heatMap?.flyTo({
          center: [centerLng, centerLat],
          duration: 800,
        });
      }

      return;
    }

    // if no select code then assume all selection closed and reset map
    if (!selectedCountryCode) {
      heatMap?.flyTo({
        center: [0, 20],
        essential: true,
        zoom: 0.7,
        screenSpeed: 2,
      });
    }
  }, [countryHeatMapMetaData, heatMap, selectedCountryCode]);

  const handleMapClick = useCallback(
    async ({ lngLat }: MapLayerMouseEvent) => {
      try {
        const geoCodeData: MapBoxGecodingResponse = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngLat.lng},${lngLat.lat}.json?types=country&access_token=${mapBoxToken}`,
        ).then(data => data.json());

        const countryIso3166_2Code =
          geoCodeData.features[0]?.properties.short_code;

        const countryMetaData =
          countryHeatMapMetaData?.[countryIso3166_2Code?.toUpperCase() ?? ''];

        if (!countryIso3166_2Code || !countryMetaData) {
          // if unable to find country and related metadata just move map to click location as assume not clicked on a country or one included in data set
          moveMapToLongLat([lngLat.lng, lngLat.lat]);
          setShowCountryCard(false);
          return;
        }

        setSelectedCountryCode(countryIso3166_2Code.toUpperCase());
        setShowCountryCard(true);
        updateSelectedNavigationItem(undefined);
      } catch (exception) {
        console.error(
          `unable to get geo code data for [${lngLat.lng}, ${lngLat.lat}] coordinates`,
          exception,
        );
      }
    },
    [
      countryHeatMapMetaData,
      moveMapToLongLat,
      setSelectedCountryCode,
      setShowCountryCard,
      updateSelectedNavigationItem,
    ],
  );

  // offset the map when the info box is open
  useEffect(() => {
    const boxWidth = offsetCard ? 300 : 0;
    heatMap?.easeTo(
      {
        padding: { top: 0, left: boxWidth, right: 0, bottom: 0 },
        duration: 1000,
      },
      {
        offsetChanged: true,
      },
    );
  }, [offsetCard, heatMap]);

  if (errorFetchingMapData) {
    // handle errors requesting data here (should be extremely rare)
    return null;
  }

  return (
    <MapContainer data-tour="heatmap-map-wrapper">
      <MapBox
        interactiveLayerIds={interactiveLayerIds}
        onMouseMove={e => {
          const feature = e.features && e.features[0];
          const iso2 = feature?.properties?.iso_3166_1 ?? null;
          setHoveredIso2(iso2);
          if (e.target && 'getCanvas' in e.target) {
            e.target.getCanvas().style.cursor = iso2 ? 'pointer' : '';
          }
        }}
        onMouseLeave={() => {
          setHoveredIso2(null);
          heatMap?.getCanvas?.().style &&
            (heatMap.getCanvas().style.cursor = '');
        }}
        initialViewState={{
          longitude: 0,
          latitude: 20,
          zoom: 0.7,
        }}
        id="heatMap"
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/lcpn-interactive-map/cl70htojf000u14p783pk7gph"
        mapboxAccessToken={mapBoxToken}
        maxZoom={5}
        minZoom={0.7}
        dragRotate={false}
        onClick={handleMapClick}
        onMoveEnd={(event: ViewStateChangeEvent & mapboxgl.EventData) => {
          if (event.originalEvent) {
            // do nothing
          } else if (event.offsetChanged !== true) {
            // check the offset was not changed (indicated by custom event data)
            // to debounce
            setOffsetCard(true);
          }
        }}
      >
        <HeatMapSource
          rangeType={rangeType}
          filterTypeSex={filterTypeSex}
          filterTypeStatus={filterTypeStatus}
          filterOptions={filterOptions}
          hoveredIso2={hoveredIso2}
          selectedIso2={countryInfo?.countryIso3166_1_2Name ?? null}
        />
        <NavigationControl showCompass={false} position={'bottom-left'} />
      </MapBox>

      {!isNccpMode && !isStatusMode && <HeatMapStatusFilter />}
      {isNccpMode && <HeatMapYearTimeline mode="nccp" />}
      {isStatusMode && <HeatMapYearTimeline mode="status" />}

      <HeatMapModeToggle
        rangeFilterType={rangeType}
        setRangeFilterType={setRangeType}
      />

      {isNccpMode ? (
        <NccpLegend />
      ) : isStatusMode ? (
        <StatusLegend />
      ) : (
        legendConfig && (
          <HeatmapLegend
            legendConfig={legendConfig}
            title={rangeType}
            description={legendMeta.description}
            filterType={filterTypeSex}
            setFilterType={setFilterTypeSex}
          />
        )
      )}

      {showCountryCard && countryInfo && selectedCountryCode && (
        <div
          style={{
            position: 'absolute',
            top: `max(15%, ${theme.spacing(14)})`,
            left: theme.spacing(15),
          }}
        >
          <CountryCard
            countryName={countryInfo.countryName}
            countryCode={selectedCountryCode}
            closeCard={() => {
              setShowCountryCard(false);
              setOffsetCard(false);
            }}
            currentExperience="heatmap"
            minWidth={isNccpMode || isStatusMode ? '18rem' : undefined}
          >
            {isNccpMode ? (
              <>
                <Typography variant="p2">
                  <ul
                    style={{
                      display: 'flex',
                      marginTop: theme.spacing(2),
                      flexDirection: 'column',
                      gap: theme.spacing(0),
                    }}
                  >
                    {(
                      [
                        {
                          value: countryInfo.nccpAvailable,
                          key: 'nccpLegend.hasNccp',
                          icon: 'FactCheck',
                        },
                        {
                          value: countryInfo.lungCancerInNccp,
                          key: 'nccpLegend.lungCancerInNccp',
                          icon: 'Lungs',
                        },
                        {
                          value: countryInfo.earlyDetectionInNccp,
                          key: 'nccpLegend.earlyDetectionInNccp',
                          icon: 'EarlyDetection',
                        },
                        {
                          value: countryInfo.fundingForEarlyDetection,
                          key: 'nccpLegend.fundingForEarlyDetection',
                          icon: 'Funding',
                        },
                      ] as {
                        value: string | undefined;
                        key: string;
                        icon: IconVariant;
                      }[]
                    ).map(({ value, key, icon }) => (
                      <li
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing(2),
                        }}
                      >
                        <Icon
                          variant={value === 'Yes' ? 'Tick' : 'Cross'}
                          width={14}
                          height={14}
                          style={{
                            color: value === 'Yes' ? '#008b7f' : '#c62828',
                            flexShrink: 0,
                          }}
                        />
                        <Icon
                          variant={icon}
                          width={18}
                          height={18}
                          style={{ flexShrink: 0 }}
                        />
                        <span>{t(key)}</span>
                      </li>
                    ))}
                  </ul>
                </Typography>
                {countryInfo.nccpLastUpdated && (
                  <Typography variant="p3">
                    {t('nccpLegend.lastUpdated', {
                      year: countryInfo.nccpLastUpdated,
                    })}
                  </Typography>
                )}
              </>
            ) : isStatusMode ? (
              <Stack sx={{ mt: 1 }} spacing={1}>
                <Typography variant="p2">
                  {t('statusLegend.currentStatus')}:{' '}
                  {t(
                    `countryStatus.${
                      getStatusConfigByDataValue(countryInfo.countryStatus)
                        ?.translationKey ?? 'unknownStatus'
                    }`,
                  )}
                </Typography>
                {countryInfo.statusChangesOverTime && (
                  <Typography
                    variant="p2"
                    style={{ marginTop: theme.spacing(2) }}
                  >
                    {t('statusLegend.formalCommitmentYear', {
                      year: countryInfo.statusChangesOverTime,
                    })}
                  </Typography>
                )}
                {countryInfo.currentCountryStatusYear && (
                  <Typography variant="p2">
                    {t('statusLegend.currentStatusYear', {
                      year: countryInfo.currentCountryStatusYear,
                    })}
                  </Typography>
                )}
              </Stack>
            ) : (
              <>
                <Typography variant="p3">
                  <>
                    {t('heatMap.lungCancer', {
                      replace: {
                        type: rangeType === 'incidence' ? 'cases' : 'deaths',
                      },
                    })}
                    <br />
                    {t('heatMap.perPopulation')}
                  </>
                </Typography>
                <Typography variant="p2">
                  <ul>
                    <li style={{ marginBottom: 0 }}>
                      {filterTypeSex === 'Combined' && (
                        <span>{t('heatMap.genders.bothSexes')}:&nbsp;</span>
                      )}
                      {filterTypeSex === 'Male' && (
                        <span>{t('heatMap.genders.male')}:&nbsp;</span>
                      )}
                      {filterTypeSex === 'Female' && (
                        <span>{t('heatMap.genders.female')}:&nbsp;</span>
                      )}
                      <span style={{ color: '#5A5A5A' }}>
                        {countryInfo[`${rangeType}${filterTypeSex}`] > 0
                          ? countryInfo[`${rangeType}${filterTypeSex}`].toFixed(
                              1,
                            )
                          : t('heatMap.noData')}
                      </span>
                    </li>
                  </ul>
                </Typography>
                <Typography variant="p3">
                  {t('heatMap.globocan', { year: countryInfo.globocanYear })}
                </Typography>
              </>
            )}
          </CountryCard>
        </div>
      )}
    </MapContainer>
  );
}
